import React from "react";
import { useRefinementList, Configure } from "react-instantsearch-hooks-web";
import { useTheme } from "../context/ThemeContext";
import { useTranslation } from "react-i18next";

// Parse "YYYY/MM/DD" into a UTC timestamp
function parseYMD(label) {
  const m = /^(\d{4})[/-](\d{2})[/-](\d{2})$/.exec(label);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1; // 0..11
  const d = Number(m[3]);
  return Date.UTC(y, mo, d, 0, 0, 0, 0);
}

/**
 * Date facet (sorted newest → oldest).
 * Uses Algolia facet values for `data.dateAdded` and orders them on the client.
 */
export default function RefinementDateFacet({ title }) {
  const { t } = useTranslation();
  const { theme, isForest } = useTheme();
  const { items, refine, canRefine } = useRefinementList({
    attribute: "data.dateAdded",
    operator: "or",
    limit: 200, // show plenty of days
  });

  // Sort by parsed timestamp desc; unknown formats fall back to bottom
  const sorted = React.useMemo(() => {
    const withTs = (items || []).map((it) => ({
      ...it,
      __ts: parseYMD(it.label) ?? -Infinity,
    }));
    withTs.sort((a, b) => (b.__ts - a.__ts) || a.label.localeCompare(b.label));
    return withTs;
  }, [items]);

  const wrapperStyle = {
    backgroundColor: isForest ? 'rgba(6,78,59,0.3)' : theme.surface.card,
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : theme.surface.cardBorder}`,
  };

  const titleStyle = {
    color: isForest ? '#e8f5e9' : theme.text.primary,
  };

  const emptyStyle = {
    color: isForest ? '#a5d6a7' : theme.text.tertiary,
  };

  const labelStyle = {
    color: isForest ? '#e8f5e9' : theme.text.primary,
  };

  const countStyle = {
    color: isForest ? '#a5d6a7' : theme.text.secondary,
  };

  return (
    <div className="rounded-xl" style={wrapperStyle}>
      <div className="px-3 py-3">
        <div className="text-base font-semibold" style={titleStyle}>{title || t('converters.date')}</div>
      </div>

      <div className="px-3 pb-3">
        {!canRefine || !sorted.length ? (
          <div className="text-sm" style={emptyStyle}>{t('converters.noDatedResults')}</div>
        ) : (
          <ul className="max-h-64 overflow-auto space-y-2">
            {sorted.map((item) => (
              <li key={item.label}>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-green-600 w-5 h-5"
                    checked={item.isRefined}
                    onChange={() => refine(item.value)}
                  />
                  <span className="text-sm" style={labelStyle}>
                    {item.label}
                    {typeof item.count === "number" && (
                      <span style={countStyle}> ({item.count})</span>
                    )}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Make sure we can retrieve enough facet values */}
      <Configure maxValuesPerFacet={300} />
    </div>
  );
}
